import { Fragment, type KeyboardEvent, type RefObject, type SyntheticEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { type AreaTotal, inkOn, mapAreas, NO_ESTIMATE_FILL, nextTile, SCALE_GRADIENT, scaleTicks, scaleTop, tileFill, tileHasText, tileMinSide, treemap, treemapHeight, type TreeBlock, type TreeTile } from "./sizeHeat";
import { galText } from "./accounts";
import { minutesText, otherTileText, overflowsText, pickRows, type SavingsPick, type SizeRow, tileLines, tileText } from "./sizes";

const DETAIL_HINT = "Point to a tile or tap it to see its account. Arrow keys move between the tiles.";
const HEAD_UNIT_WIDTH = 72;
const HEAD_FULL_WIDTH = 210;

const detailOf = (tile: TreeTile, arrow?: string) => (tile.row ? tileText(tile.row, arrow) : otherTileText(tile));

function headText({ w, gallons, minutes }: TreeBlock) {
  if (w >= HEAD_FULL_WIDTH) return `${galText(gallons)} · ${minutesText(minutes)} min`;
  return w < HEAD_UNIT_WIDTH ? minutesText(minutes) : `${minutesText(minutes)} min`;
}

function setStop(tree: Element, target: HTMLButtonElement) {
  for (const button of tree.querySelectorAll("button")) button.tabIndex = -1;
  target.tabIndex = 0;
}

/** Keep one tab stop in the treemap: the arrow keys, Home and End move it across all its buttons. */
function moveFocus(event: KeyboardEvent<HTMLDivElement>) {
  const buttons = [...event.currentTarget.querySelectorAll("button")];
  const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
  const next = nextTile(event.key, at, buttons.length);
  if (next === null || at < 0) return;
  const target = buttons[next];
  event.preventDefault();
  setStop(event.currentTarget, target);
  target.focus();
}

/** Rule W-5: a tiled box of the containers. Area is gallons, colour is minutes saved a month. */
export function SizeTreemap({
  rows,
  pick,
  onPick,
  onClear,
  onTotals,
  back,
}: {
  rows: readonly SizeRow[];
  pick: SavingsPick;
  onPick: (pick: SavingsPick) => void;
  onClear: () => void;
  onTotals: (() => void) | null;
  back: { tile: string | null } | null;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [detail, setDetail] = useState<string | null>(null);
  const areas = useMemo(() => mapAreas(rows), [rows]);
  const top = useMemo(() => scaleTop(rows), [rows]);
  const ticks = useMemo(() => scaleTicks(top), [top]);
  const drilled = areas.find((area) => area.area === pick.area);
  const drill = drilled ? drilled.area : null;
  const mapRows = useMemo(() => (drill === null ? rows : pickRows(rows, { area: drill, account: null })), [rows, drill]);
  const height = treemapHeight(width, drilled ? 1 : areas.length, drilled ? drilled.swaps : areas.reduce((sum, area) => sum + area.swaps, 0));
  const blocks = useMemo(() => (width > 0 ? treemap(mapRows, width, height, tileMinSide(width)) : []), [mapRows, width, height]);
  const details = useMemo(() => new Map(blocks.flatMap((block) => block.tiles.map((tile) => [tile.key, detailOf(tile)] as const))), [blocks]);
  const hasTiles = areas.length > 0;
  const hasOther = blocks.some((block) => block.tiles.some((tile) => tile.row === null));

  useLayoutEffect(() => {
    const target = box.current;
    if (!target) return;
    const measure = () => setWidth(target.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(measure);
    watch.observe(target);
    return () => watch.disconnect();
  }, [hasTiles]);

  useEffect(() => {
    const tree = box.current;
    if (!back || !tree) return;
    const stop = tree.querySelector<HTMLButtonElement>(back.tile === null ? 'button[tabindex="0"]' : `[data-tile="${CSS.escape(back.tile)}"]`);
    const target = stop ?? tree.querySelector("button");
    if (!target) return;
    setStop(tree, target);
    target.scrollIntoView({ block: "center" });
    target.focus({ preventScroll: true });
  }, [back]);

  const show = (event: SyntheticEvent) => {
    const key = (event.target as Element).closest<HTMLElement>("[data-tile]")?.dataset.tile;
    setDetail(key === undefined ? null : (details.get(key) ?? null));
  };

  const tree = useMemo(
    () =>
      blocks.map((block, blockIndex) => (
        <div key={block.area} className="size-tree-block" role="group" aria-label={block.area} data-testid="size-tree-block" data-area={block.area} style={{ left: block.x, top: block.y, width: block.w, height: block.h }}>
          <button
            type="button"
            className="size-tree-head"
            data-testid="size-tree-area"
            tabIndex={blockIndex === 0 ? 0 : -1}
            aria-pressed={pick.area === block.area}
            aria-label={`${block.area}: ${galText(block.gallons)}, ${minutesText(block.minutes)} min a month. Show this area in the table`}
            onClick={() => onPick({ area: block.area, account: null })}
          >
            <span className="size-tree-area">{block.area}</span>
            <span>{headText(block)}</span>
          </button>
          {block.tiles.map((tile) => {
            const other = tile.row === null;
            const fill = tileFill(tile, top);
            const lines = tileHasText(tile) ? tileLines(tile) : null;
            return (
              <button
                key={tile.key}
                type="button"
                className="size-tree-tile"
                data-testid={other ? "size-tree-other" : "size-tree-tile"}
                data-tile={tile.key}
                data-fill={fill}
                tabIndex={-1}
                aria-pressed={!other && pick.account === tile.key}
                aria-label={`${detailOf(tile, "to")}. ${other ? "Show this area in the table" : "Show this account in the table"}`}
                style={{ left: tile.x - block.x, top: tile.y - block.y, width: tile.w, height: tile.h, background: fill, color: inkOn(fill) }}
                onClick={() => onPick(tile.row ? { area: drill, account: tile.key } : { area: tile.area, account: null })}
              >
                {lines && (
                  <>
                    <span data-testid={lines.change ? "size-tree-change" : undefined}>{lines.first}</span>
                    {lines.second && <span>{lines.second}</span>}
                  </>
                )}
              </button>
            );
          })}
        </div>
      )),
    [blocks, pick, onPick, drill, top],
  );

  return (
    <section className="size-heat" data-testid="size-heat" aria-label="Container gallons and minutes saved">
      {hasTiles ? (
        <div>
          <ul className="size-tree-legend" aria-label="Treemap legend">
            <li>Tile size: gallons of the container.</li>
            <li>Colour: minutes a month that the best size saves. Red is the highest on this page, not a fixed amount.</li>
            {hasOther && <li data-testid="size-tree-other-key">Other: small tiles with no saving.</li>}
            <li className="size-tree-scale" data-testid="size-tree-scale">
              <span className="size-tree-bar" style={{ background: SCALE_GRADIENT }} aria-hidden="true" />
              <span className="size-tree-ticks">
                {ticks.map((tick) => (
                  <span key={tick.value} style={{ left: `${tick.at * 100}%` }} data-end={tick.at === 0 ? "start" : tick.at === 1 ? "end" : undefined}>
                    {minutesText(tick.value)}
                  </span>
                ))}
              </span>
              <span className="size-tree-unit">min a month</span>
            </li>
            <li>
              <span className="size-tree-swatch" style={{ background: NO_ESTIMATE_FILL }} aria-hidden="true" />
              No estimate yet
            </li>
          </ul>
          <p className="sub size-tree-detail" data-testid="size-tree-detail">
            {detail ?? DETAIL_HINT}
          </p>
          <p className="size-tree-crumb" data-testid="size-tree-crumb">
            <span className="size-tree-path">
              {drill === null ? (
                "All areas"
              ) : (
                <>
                  <button type="button" className="link" data-testid="size-tree-all" onClick={onClear}>
                    All areas
                  </button>
                  {" > "}
                  {drill}
                </>
              )}
            </span>
            {onTotals && (
              <button type="button" className="link" data-testid="size-totals-link" onClick={onTotals}>
                Area totals
              </button>
            )}
          </p>
          <div
            ref={box}
            className="size-tree"
            data-testid="size-tree"
            role="group"
            aria-label="Treemap of the container gallons"
            style={{ height }}
            onKeyDown={moveFocus}
            onMouseOver={show}
            onMouseLeave={() => setDetail(null)}
            onFocus={show}
          >
            <Fragment key={drill}>{tree}</Fragment>
          </div>
        </div>
      ) : (
        <p data-testid="size-tree-empty">No account has a container.</p>
      )}
    </section>
  );
}

/** The swaps, the minutes and the overflows of each area with a swap. A row shows only that area. */
export function SizeAreaTotals({ totals, head, onPick }: { totals: readonly AreaTotal[]; head: RefObject<HTMLHeadingElement>; onPick: (pick: SavingsPick) => void }) {
  if (totals.length === 0) return null;
  return (
    <section aria-labelledby="size-area-totals">
      <h2 id="size-area-totals" className="size-area-totals" data-testid="size-totals-head" tabIndex={-1} ref={head}>
        Area totals
      </h2>
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Totals of each area">
        <table className="list" data-testid="size-area-totals">
          <thead>
            <tr>
              <th scope="col">Area</th>
              <th scope="col" className="num">
                Swaps
              </th>
              <th scope="col" className="num">
                Minutes a month
              </th>
              <th scope="col" className="num">
                Overflows avoided a month
              </th>
            </tr>
          </thead>
          <tbody>
            {totals.map((total) => (
              <tr key={total.area} data-testid="size-area-row" data-area={total.area}>
                <th scope="row">
                  <button type="button" className="link" data-testid="size-area-open" onClick={() => onPick({ area: total.area, account: null })}>
                    {total.area}
                  </button>
                </th>
                <td className="num" data-label="Swaps">
                  {total.accounts.toLocaleString("en-US")}
                </td>
                <td className="num" data-label="Minutes a month">
                  {minutesText(total.minutes)}
                </td>
                <td className="num" data-label="Overflows avoided a month">
                  {overflowsText(total.overflows)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
